import moment from "moment";
import React from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";
import { CHART_TIME_LABELS } from "src/constants/common";
import {
  SHORT_DATE_WITH_MONTH_DAY,
  SHORT_DATE_WITH_HOUR_MINUTE,
  DATE_FORMAT_STRING_WITH_YEAR_FIRST,
  DATE_FORMAT_STRING_FOR_HOUR_MINUTE_AM_PM,
} from "src/constants/dataTimeFormats";

interface MedicationChartProps {
  /** Schedule items: { date, time, type: "Taken" | "Missed" | "Scheduled" } */
  data?: any;
}

const MedicationChart: React.FC<MedicationChartProps> = ({ data }) => {
  const hasData = data && data.length > 0;
  // Helper function to convert time string to numeric value
  const timeToNumeric = (timeStr: string): number => {
    const parts = timeStr.split(":");
    const hours = parseFloat(parts[0]);
    const minutes = parts[1] ? parseFloat(parts[1]) : 0;
    return hours + minutes / 60;
  };

  // Generate 7 days starting from today
  const generate7Days = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(
        moment().add(i, "days").format(DATE_FORMAT_STRING_WITH_YEAR_FIRST)
      );
    }
    return days;
  };

  // Process data and ensure 7 days are always shown
  const processChartData = () => {
    const sevenDays = generate7Days();

    // If no data, create placeholder data for all 7 days (invisible but maintains x-axis)
    if (!data || data.length === 0) {
      return sevenDays.map((date) => ({
        date: moment(date, DATE_FORMAT_STRING_WITH_YEAR_FIRST).format(
          SHORT_DATE_WITH_MONTH_DAY
        ),
        dateRaw: date,
        timeNumeric: null,
        time: null,
        timeLabel: null,
        type: null,
      }));
    }

    // Map existing data with formatted dates (support YYYY-MM-DD or MMM DD from API)
    const dataByDate = new Map();
    const dateFormats = [
      DATE_FORMAT_STRING_WITH_YEAR_FIRST,
      SHORT_DATE_WITH_MONTH_DAY,
      "DD MMM",
    ];
    data.forEach((item: any) => {
      const m = moment(item.date, dateFormats, true);
      if (!m.isValid()) return;
      const formattedDate = m.format(SHORT_DATE_WITH_MONTH_DAY);
      if (!dataByDate.has(formattedDate)) {
        dataByDate.set(formattedDate, []);
      }
      dataByDate.get(formattedDate).push({
        ...item,
        timeNumeric: item.time ? timeToNumeric(item.time) : null,
        date: formattedDate,
        dateRaw: item.date,
        timeLabel: item.time
          ? moment(
              item.time,
              [SHORT_DATE_WITH_HOUR_MINUTE, "hh:mm A"],
              true
            ).format(DATE_FORMAT_STRING_FOR_HOUR_MINUTE_AM_PM)
          : null,
      });
    });

    // Create full 7-day dataset with placeholders for missing days
    const processedData: any[] = [];
    sevenDays.forEach((date) => {
      const formattedDate = moment(
        date,
        DATE_FORMAT_STRING_WITH_YEAR_FIRST
      ).format(SHORT_DATE_WITH_MONTH_DAY);

      if (dataByDate.has(formattedDate)) {
        // Add all entries for this date
        processedData.push(...dataByDate.get(formattedDate));
      } else {
        // Add placeholder for empty date to maintain x-axis spacing
        processedData.push({
          date: formattedDate,
          dateRaw: date,
          timeNumeric: null,
          time: null,
          timeLabel: null,
          type: null,
        });
      }
    });

    return processedData;
  };

  const chartData = processChartData();

  // Generate x-axis ticks (always 7 days)
  const xAxisTicks = generate7Days().map((date) =>
    moment(date, DATE_FORMAT_STRING_WITH_YEAR_FIRST).format(
      SHORT_DATE_WITH_MONTH_DAY
    )
  );

  const MedicationStatus = ["Taken", "Missed", "Scheduled"];

  const COLORS = {
    Taken: "#1A73E8",
    Scheduled: "#fff",
    Missed: "#EA4335",
  };

  const tickFormatter = (tick: number) => {
    if (tick === 0 || tick === 24) return "12am"; // Midnight
    if (tick === 4) return "4am"; // Early morning
    if (tick === 8) return "8am"; // Morning
    if (tick === 12) return "12pm"; // Noon
    if (tick === 16) return "4pm"; // Afternoon
    if (tick === 20) return "8pm"; // Evening
    return ""; // fallback (shouldn’t normally hit)
  };

  const renderLabelList = (props: any, type: string) => {
    const { x, y, value } = props;

    // Don't render labels for null/undefined values (placeholder data)
    if (!value) {
      return null;
    }

    const timeNumeric = moment(value, "hh:mm A").hour();
    const offsetY = timeNumeric >= 12 ? 22 : -15;
    return (
      <g>
        <text
          x={x + 4}
          y={y + offsetY}
          textAnchor="middle"
          style={{
            fontSize: 10,
            fill:
              COLORS[type as keyof typeof COLORS] === "#fff"
                ? "#1A73E8"
                : COLORS[type as keyof typeof COLORS],
            fontWeight: 500,
            background: "white",
          }}
        >
          {value.substring(0, 5)}
        </text>
        <text
          x={x + 4}
          y={y + offsetY + 10}
          textAnchor="middle"
          style={{
            fontSize: 10,
            fill:
              COLORS[type as keyof typeof COLORS] === "#fff"
                ? "#1A73E8"
                : COLORS[type as keyof typeof COLORS],
            fontWeight: 500,
            background: "white",
          }}
        >
          {value.substring(5)}
        </text>
      </g>
    );
  };

  return (
    <div className="w-full space-y-4 relative">
      <div className="absolute left-0 top-0 w-14 h-full bg-gray-50 z-[5] flex flex-col justify-end">
        <div className="flex flex-col justify-between gap-y-[7px] mb-[75px]">
          {CHART_TIME_LABELS?.map((time) => (
            <span key={time} className="text-[11px] font-normal text-gray-500">
              {time}
            </span>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <div style={{ minWidth: 500, marginLeft: -8 }}>
          {!hasData && (
            <div className="h-[180px] flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
              <p className="text-sm text-gray-500">No doses this week</p>
            </div>
          )}
          {hasData && (
            <ResponsiveContainer width={"100%"} height={180}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 20, right: 20 }}
                  fontSize={12}
                  style={{ textTransform: "capitalize" }}
                  allowDuplicatedCategory={false}
                  domain={xAxisTicks}
                  ticks={xAxisTicks}
                />
                <YAxis
                  dataKey="timeNumeric"
                  type="number"
                  domain={[0, 24]}
                  ticks={[24, 20, 16, 12, 8, 4, 0]}
                  tickFormatter={(tick) => tickFormatter(tick)}
                  axisLine={false}
                  tickLine={false}
                  fontSize={12}
                  orientation="left"
                  visibility="hidden"
                />
                {/* <Tooltip
                formatter={(value, name, props) => [
                  `${props.payload.time}`,
                  "Time",
                ]}
              /> */}
                {MedicationStatus?.map((type) => {
                  const filteredData = chartData?.filter(
                    (item: any) => item.type === type
                  );
                  // Add placeholders for all 7 days even in filtered data
                  const fullData = xAxisTicks.map((tick) => {
                    const existing = filteredData.find(
                      (item: any) => item.date === tick
                    );
                    return (
                      existing || {
                        date: tick,
                        timeNumeric: null,
                        type: null,
                      }
                    );
                  });

                  return (
                    <Scatter
                      key={type}
                      name={type}
                      data={fullData}
                      fill={
                        type === "Scheduled"
                          ? "#1A73E8"
                          : COLORS[type as keyof typeof COLORS]
                      }
                      shape="circle"
                      stroke={
                        type === "Scheduled"
                          ? "#1A73E8"
                          : COLORS[type as keyof typeof COLORS]
                      }
                      strokeWidth={2}
                      fillOpacity={type === "Scheduled" ? 0 : 1}
                    >
                      <LabelList
                        dataKey="timeLabel"
                        content={(props: any) => renderLabelList(props, type)}
                      />
                    </Scatter>
                  );
                })}
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="flex flex-row items-center justify-center gap-4 border-t border-gray-200 pt-4">
        <div className="flex flex-row items-center justify-center gap-1">
          <div
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: COLORS.Scheduled,
              border: `1px solid ${COLORS.Taken}`,
            }}
          ></div>
          <span className="text-xs font-light">Scheduled</span>
        </div>
        <div className="flex flex-row items-center justify-center gap-1">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: COLORS.Taken }}
          ></div>
          <span className="text-xs font-light">Taken</span>
        </div>
        <div className="flex flex-row items-center justify-center gap-1">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: COLORS.Missed }}
          ></div>
          <span className="text-xs font-light">Missed</span>
        </div>
      </div>
    </div>
  );
};

export default MedicationChart;
